"use client";

import React from "react";
import { Button, ConfigProvider } from "antd";
import type { ButtonProps } from "antd";
import { orionTheme } from "../theme/orion-theme";
import clsx from "clsx";
import { MoreOutlined } from "@ant-design/icons";
import "../styles/more-info-button.css";

export interface MoreInfoButtonProps extends Omit<ButtonProps, "icon"> {
  /**
   * Additional CSS classes (supports Tailwind overrides)
   */
  className?: string;
}

/**
 * MoreInfoButton component - Three-dot menu button with OpenStore Orion theming
 *
 * This component provides a three-dot menu button based on Ant Design Button
 * with OpenStore Orion theme and Figma design specifications.
 *
 * Features:
 * - Figma design specifications (24x24px, specific colors, hover states)
 * - Automatic theming
 * - Tailwind class override support
 * - ALL Ant Design Button features and props
 *
 * @example
 * ```tsx
 * // Basic more info button
 * <MoreInfoButton onClick={handleClick} />
 *
 * // With custom props
 * <MoreInfoButton
 *   onClick={handleClick}
 *   disabled={isDisabled}
 *   loading={isLoading}
 *   className="custom-more-info"
 * />
 * ```
 */
export const MoreInfoButton: React.FC<MoreInfoButtonProps> = ({
  className,
  ...props
}) => {
  return (
    <ConfigProvider theme={orionTheme}>
      <Button
        type="text"
        shape="circle"
        size="small"
        icon={<MoreOutlined />}
        className={clsx("orion-more-info-button", className)}
        {...props}
      />
    </ConfigProvider>
  );
};

MoreInfoButton.displayName = "MoreInfoButton";
